import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback } from 'react';

import { CategoryModuleCard } from '@/components/CategoryModule/components/CategoryModuleCard/CategoryModuleCard';
import useCalculateCategoriesCount from '@/hooks/useCalculateCategoriesCount';
import { useAppSelector } from '@/redux/hooks';
import { featuredCategoriesSelector, featuredCategoriesTitleSelector } from '@/redux/modules/featuredCategories';
import { FeaturedCategory } from '@/types/FeaturedCategory';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { CategoryCard } from './components/CategoryCard/CategoryCard';

type Props = {
    className?: string;
    shouldUseHomePageRedesign?: boolean;
    testid?: string;
};

export const CategoryModule = ({ className, shouldUseHomePageRedesign = false, testid }: Props) => {
    const formatMessage = useAppFormatMessage();
    const title = useAppSelector(featuredCategoriesTitleSelector);
    const categories = useAppSelector(featuredCategoriesSelector);
    const { categoryCount, isMobile } = useCalculateCategoriesCount();
    const categoriesToShow = categories.slice(0, categoryCount);
    const isTwoColumnLayout = shouldUseHomePageRedesign && isMobile;

    const buildCategoryCards = useCallback(
        ({ imageUrl, link, title }: FeaturedCategory) => {
            if (shouldUseHomePageRedesign) {
                return (
                    <CategoryModuleCard
                        imageUrl={imageUrl}
                        isTwoColumnLayout={isTwoColumnLayout}
                        key={title}
                        link={link}
                        title={title}
                    />
                );
            }
            return (
                <CategoryCard
                    imageUrl={imageUrl}
                    key={title}
                    link={link}
                    title={title}
                />
            );
        },
        [isTwoColumnLayout, shouldUseHomePageRedesign]
    );

    return categoriesToShow.length ? (
        <div
            className={cn('flex w-full flex-col items-center self-center text-center', className)}
            data-testid={testid}
        >
            <H2
                className={cn('w-full text-left', {
                    'mb-sm text-center': shouldUseHomePageRedesign,
                })}
            >
                {title || formatMessage(['browse_by_category'])}
            </H2>
            <div
                className={cn('flex w-full flex-wrap justify-between overflow-hidden', {
                    'gap-sm': shouldUseHomePageRedesign,
                })}
            >
                {categoriesToShow.map(buildCategoryCards)}
            </div>
        </div>
    ) : null;
};
